import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  TextField,
  Typography,
  Paper,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { GetApp } from '@material-ui/icons';
import { NEXT, PREVIOUS, FINALIZE } from '../../../hooks/useStepNavigations';
import { formatLocaleDate } from '../../../common/utils';
import { Actions as ShoreSModel } from '../../../store/ducks/shoreSModel';
import { parseShoreSModelFile, reprojectFeatureCollection } from '../../../algorithms/shoreS';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import GoogleMap from '../map/GoogleMap';

const COASTLINES_STYLE = { strokeColor: '#2196F3', strokeWeight: 2, strokeOpacity: 0.9, fillOpacity: 0 };
const ROI_STYLE = {
  strokeColor: '#FF9800',
  strokeWeight: 2,
  strokeOpacity: 0.9,
  fillColor: '#FF9800',
  fillOpacity: 0.05,
  clickable: false,
};


const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(2, 3),
    margin: theme.spacing(2, 2, 0),
  },
  field: {
    width: '100%',
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  detectedRange: {
    marginTop: theme.spacing(0.5),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  uploadRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  mapWrapper: {
    margin: theme.spacing(0, 2),
    height: '50vh',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    position: 'relative',
  },
  legend: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    zIndex: 5,
    background: 'rgba(255,255,255,0.9)',
    borderRadius: 4,
    padding: '4px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    pointerEvents: 'none',
  },
  legendTitle: {
    fontSize: '0.72rem',
    fontWeight: 600,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.72rem',
  },
  legendDot: {
    width: 16,
    height: 3,
    borderRadius: 2,
    flexShrink: 0,
  },
}));

/**
 * @function FileUploadField
 * @description Labeled file-picker button with a hint line and a chip showing the currently
 * selected file's name, if any.
 * @param {Object} props - Component props.
 * @param {string} props.label
 * @param {string[]} props.hints - Hint lines rendered under the picker (one per accepted format).
 * @param {string} props.accept - HTML `accept` attribute (allowed file extensions).
 * @param {File|{name: string}|null} props.file - Currently selected file (or a restored stand-in).
 * @param {Function} props.onChange - Called with the newly selected File (or null).
 * @param {Object} props.classes - `useStyles` classes.
 * **Called by:
 * @see {ShoreSDataChooser}
 * @memberof module:ShoreS
 * @returns {JSX.Element} The file upload field.
 */
const FileUploadField = ({ label, hints, accept, file, onChange, classes }) => (
  <Box className={classes.uploadRow}>
    <Typography variant="subtitle2">{label}</Typography>
    <Button variant="outlined" component="label" size="small" style={{ alignSelf: 'flex-start' }}>
      Selecionar arquivo
      <input type="file" accept={accept} hidden onChange={(e) => onChange(e.target.files[0] || null)} />
    </Button>
    {file && (
      <Chip label={file.name} size="small" variant="outlined" color="primary" style={{ alignSelf: 'flex-start', maxWidth: '100%' }} />
    )}
    {hints.map((line) => (
      <Typography key={line} className={classes.hint}>
        {line}
      </Typography>
    ))}
  </Box>
);

/**
 * @function ShoreSDataChooser
 * @description Shore-S's model-upload step: CRS input plus a `.mat`/CSV model file, debounce-
 * parsed via `parseShoreSModelFile` into modeled coastlines + derived ROI, previewed on a map. If
 * the model carries temporal data, its date range is captured silently (no visible field) and
 * persisted for the period step, which is where the search period is actually chosen and bounded.
 * Restores its full state (file name, CRS, dates) from `shoreSModel`/sessionStorage on remount, so
 * navigating back doesn't force a re-upload.
 * @param {Object} props - Component props.
 * @param {Function} props.navigate - Function to navigate between steps.
 * **Called by:
 * @see {ShoresSAcquisitionPage}
 * @memberof module:ShoreS
 * @returns {JSX.Element} The ShoreSDataChooser component.
 */
const ShoreSDataChooser = ({ navigate }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const mapRef = useRef(null);
  const layerRefs = useRef({ coastlines: null, roi: null });
  const [mapReady, setMapReady] = useState(false);

  const shoreSModel = useSelector((state) => state.shoreSModel);
  const hasRestorableModel = Boolean(shoreSModel?.modeledCoastlinesGeoJSON || shoreSModel?.roiBBox);

  const [crs, setCrs] = useState(() => (hasRestorableModel ? shoreSModel.crs || sessionStorage.getItem('shoreSCrs') || '' : ''));
  const [modelFile, setModelFile] = useState(() => {
    if (!hasRestorableModel) return null;
    const name = sessionStorage.getItem('shoreSModelFileName');
    return { name: name || t('forms.shoreS.acquisition.dataChooser.restoredFileName') };
  });
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [parsedModel, setParsedModel] = useState(() => (hasRestorableModel ? shoreSModel : null));
  const [dateStart, setDateStart] = useState(() => (hasRestorableModel ? sessionStorage.getItem('shoreSDateStart') || '' : ''));
  const [dateEnd, setDateEnd] = useState(() => (hasRestorableModel ? sessionStorage.getItem('shoreSDateEnd') || '' : ''));
  const skipNextParseRef = useRef(hasRestorableModel);
  const skipNextDateSeedRef = useRef(hasRestorableModel);

  // The search period isn't picked here — it's captured silently from the model's own temporal
  // range and handed to the period step (see handleContinue), which owns the date selection and
  // its DATE_LIMIT_MONTHS bounds. Dates restored from sessionStorage win on remount, so a period
  // already narrowed downstream isn't reset by walking back to this step.
  useEffect(() => {
    if (skipNextDateSeedRef.current) {
      skipNextDateSeedRef.current = false;
      return;
    }
    if (parsedModel?.hasTemporalData && parsedModel.dateRange) {
      setDateStart(parsedModel.dateRange.start);
      setDateEnd(parsedModel.dateRange.end);
    } else {
      setDateStart('');
      setDateEnd('');
    }
  }, [parsedModel]);

  const canContinue = crs.trim() !== '' && modelFile !== null && parsedModel !== null && !parsing;

  
  useEffect(() => {
    if (!mapReady || !mapRef.current) return undefined;

    if (layerRefs.current.coastlines) {
      layerRefs.current.coastlines.setMap(null);
      layerRefs.current.coastlines = null;
    }
    if (layerRefs.current.roi) {
      layerRefs.current.roi.setMap(null);
      layerRefs.current.roi = null;
    }

    if (!parsedModel) return undefined;

    const bounds = new window.google.maps.LatLngBounds();

    const coastlinesToDraw =
      parsedModel.modeledCoastlinesDisplayGeoJSON ?? parsedModel.modeledCoastlinesGeoJSON;

    if (coastlinesToDraw?.features?.length) {
      try {
        // Reproject using the CRS the parse actually ran with (`parsedModel.crs`),
        // not the live `crs` field state — those can momentarily diverge if the
        // user edits the CRS again before the debounced re-parse below resolves.
        const wgs84 = reprojectFeatureCollection(coastlinesToDraw, parsedModel.crs, 'EPSG:4326');
        const layer = new window.google.maps.Data({ map: mapRef.current });
        layer.addGeoJson(wgs84);
        layer.setStyle(COASTLINES_STYLE);
        layer.forEach((feature) => {
          feature.getGeometry().forEachLatLng((latlng) => bounds.extend(latlng));
        });
        layerRefs.current.coastlines = layer;
      } catch (e) {
        console.error('[ShoreS] failed to render modeled coastlines preview', e);
      }
    }

    if (parsedModel.roiBBox) {
      const [west, south, east, north] = parsedModel.roiBBox;
      layerRefs.current.roi = new window.google.maps.Rectangle({
        bounds: { north, south, east, west },
        map: mapRef.current,
        ...ROI_STYLE,
      });
      bounds.extend({ lat: south, lng: west });
      bounds.extend({ lat: north, lng: east });
    }

    if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds);
    return undefined;
  }, [mapReady, parsedModel]);

  useEffect(() => {
    if (skipNextParseRef.current) {
      skipNextParseRef.current = false;
      return undefined;
    }

    if (!modelFile || crs.trim() === '') {
      setParsedModel(null);
      setParseError(null);
      return undefined;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      setParsing(true);
      setParseError(null);
      try {
        const result = await parseShoreSModelFile(modelFile, crs.trim());
        if (cancelled) return;
        setParsedModel(result);
        sessionStorage.setItem('shoreSCrs', crs.trim());
        sessionStorage.setItem('shoreSModelFileName', modelFile.name);
      } catch (e) {
        if (cancelled) return;
        console.error('[ShoreS] failed to parse model output file', e);
        setParsedModel(null);
        setParseError(e.message || String(e));
      } finally {
        if (!cancelled) setParsing(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [modelFile, crs]);

  const handleModelFileChange = (file) => {
    setModelFile(file);
    setParsedModel(null);
    setParseError(null);
  };

  const handleCrsChange = (value) => {
    setCrs(value);
    setParsedModel(null);
    setParseError(null);
  };

  /**
   * @function handleContinue
   * @description Commits the parsed model to the `shoreSModel` duck and persists the CRS/dates to
   * sessionStorage, then advances: to the next step if the model has temporal data (a search
   * period is needed), or straight to the result otherwise. If the ROI/CRS actually changed from
   * whatever model was previously loaded, clears the cached image search/selection so a stale
   * list from the old ROI isn't reused.
   * **Called by:
   * @see {ShoreSDataChooser} - via the "Next" button's onClick.
   * @memberof module:ShoreS
   * @returns {void}
   */
  const handleContinue = () => {
    if (!parsedModel) return;
    const isNewModel =
      JSON.stringify(parsedModel.roiBBox) !== JSON.stringify(shoreSModel?.roiBBox) || parsedModel.crs !== shoreSModel?.crs;
    if (isNewModel) {
      window.sessionStorage.removeItem('shoreSFullImageList');
      window.sessionStorage.removeItem('shoreSImageSelection');
    }

    dispatch(ShoreSModel.setModel(parsedModel));

    sessionStorage.setItem('shoreSHasTemporalData', String(parsedModel.hasTemporalData));
    sessionStorage.setItem('shoreSCrs', crs.trim());
    if (parsedModel.hasTemporalData) {
      // The dates aren't editable here anymore, so there's no field to fix an empty value in:
      // fall back to the model's own range if the restored sessionStorage entries are gone.
      sessionStorage.setItem('shoreSDateStart', dateStart || parsedModel.dateRange?.start || '');
      sessionStorage.setItem('shoreSDateEnd', dateEnd || parsedModel.dateRange?.end || '');

      sessionStorage.setItem('shoreSModelDateStart', parsedModel.dateRange?.start || '');
      sessionStorage.setItem('shoreSModelDateEnd', parsedModel.dateRange?.end || '');
      navigate(NEXT);
    } else {
      navigate(FINALIZE);
    }
  };

  return (
    <Box>
      <Paper className={classes.section} elevation={0} variant="outlined">
        <Grid container spacing={2}>

          <Grid item xs={12}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<GetApp />}
              component="a"
              href="/exemple-shore-s-data.csv"
              download>
              {t('forms.shoreS.acquisition.dataChooser.exampleCsv')}
            </Button>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t('forms.shoreS.acquisition.dataChooser.crsLabel')}
              placeholder={t('forms.shoreS.acquisition.dataChooser.crsPlaceholder')}
              value={crs}
              onChange={(e) => handleCrsChange(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.field}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
            <Typography variant="subtitle1" className={classes.sectionTitle} style={{ marginTop: 12 }}>
              {t('forms.shoreS.acquisition.dataChooser.modeledDataTitle')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <FileUploadField
              label={t('forms.shoreS.acquisition.dataChooser.modelFileLabel')}
              hints={[
                t('forms.shoreS.acquisition.dataChooser.modelFileHintMat'),
                t('forms.shoreS.acquisition.dataChooser.modelFileHintCsv'),
              ]}
              accept=".mat,.csv"
              file={modelFile}
              onChange={handleModelFileChange}
              classes={classes}
            />
          </Grid>


          <Grid item xs={12}>
            {parsing && <ActivityIndicator textual customSentence={t('forms.shoreS.acquisition.dataChooser.parsingModel')} />}
            {!parsing && parseError && (
              <Typography color="error" variant="body2">
                {t('forms.shoreS.acquisition.dataChooser.parseError')}: {parseError}
              </Typography>
            )}
            {!parsing && parsedModel?.hasTemporalData && parsedModel.dateRange && (
              <Typography variant="body2" className={classes.detectedRange}>
                {t('forms.shoreS.acquisition.dataChooser.temporalDetectedHint')}{' '}
                <b>{formatLocaleDate(parsedModel.dateRange.start)}</b>{' '}
                {t('forms.shoreS.acquisition.dataChooser.temporalDetectedSeparator')}{' '}
                <b>{formatLocaleDate(parsedModel.dateRange.end)}</b>
              </Typography>
            )}
            {!parsing && parsedModel && !parsedModel.hasTemporalData && (
              <Typography variant="body2" className={classes.hint}>
                {t('forms.shoreS.acquisition.dataChooser.noTemporalData')}
              </Typography>
            )}
            
          </Grid>
        </Grid>
      </Paper>

      {parsedModel && (
        <Box className={classes.mapWrapper}>
          <GoogleMap
            style={{ width: '100%', height: '100%' }}
            onLoad={(map) => { mapRef.current = map; setMapReady(true); }}
          />
          <Box className={classes.legend}>
            <span className={classes.legendTitle}>
              {t('forms.shoreS.acquisition.dataChooser.mapLegendTitle')}
            </span>
            {[
              { key: 'coastlines', color: COASTLINES_STYLE.strokeColor, label: t('forms.shoreS.acquisition.dataChooser.mapLegendCoastlines') },
              { key: 'roi', color: ROI_STYLE.strokeColor, label: t('forms.shoreS.acquisition.dataChooser.mapLegendRoi') },
            ].map(({ key, color, label }) => (
              <Box key={key} className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ background: color }} />
                <span>{label}</span>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box className={classes.buttons}>
        <Button variant="contained" onClick={() => navigate(PREVIOUS)}>
          {t('forms.acquisition.prev')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleContinue}
          disabled={!canContinue}
        >
          {t('forms.acquisition.next')}
        </Button>
      </Box>
    </Box>
  );
};

export default ShoreSDataChooser;
